import { ScheduleResult } from 'domains/signup/consultationSchedule/scheduleMachine';
import { DEFAULT_APPOINTMENT_TYPE, getLeadIDFromURL, setLeadIDToURL } from 'domains/signup/data/queryParams';
import { TherapyTarget } from 'domains/signup/data/types';
import { getRedirectAfterSignupV2Complete } from 'domains/signupv2/Insurance/external-forms/redirect';
import { insuranceMachineV2, InsuranceResult } from 'domains/signupv2/Insurance/insuranceMachineV2';
import { SignupLeadProgressV2 } from 'domains/signupv2/Leads/types';
import {
  createLeadLogic,
  loadLeadDataLogic,
  scheduleConsultationLogic,
  updateLead,
} from 'domains/signupv2/Parent/actors';
import { getSignupResumeCheckGuard } from 'domains/signupv2/Parent/resume';
import { scheduleMachineV2 } from 'domains/signupv2/schedule/scheduleMachineV2';
import { isString } from 'lodash';
import { MachineStateType } from 'utils/xstate/types';
import { assign, not, setup } from 'xstate';
import { miniScreenerMachine, MiniScreenerResult } from 'domains/signupv2/Mini-Screener/miniScreenerMachine';

type ScheduleParameters = {
  appointmentType: string;
  calendarID?: string;
};

// Machine's input
export type SignupMachineInput = {
  leadID?: string;
} & Partial<ScheduleParameters>;

const DEFAULT_CONFIG: SignupMachineInput = {
  appointmentType: DEFAULT_APPOINTMENT_TYPE,
};

export type CompletedSignupV2Data = {
  state: string;

  firstName: string;
  lastName: string;

  email: string;
  receiveNewsletter: boolean;

  phoneNumber: string;
  target: TherapyTarget;

  miniScreenerInfo: MiniScreenerResult['data'];
  insuranceInfo: InsuranceResult['data'];
  scheduleInfo: ScheduleResult;

  leadID: string;
  schedulingID: string;

  scheduleParameters: ScheduleParameters;
};

export type IncompleteSignupV2Data = Partial<CompletedSignupV2Data>;

type InternalContext = Partial<CompletedSignupV2Data> & {
  machineConfig: SignupMachineInput;

  /** The step which the machine resumed to */
  resumedStep?: string;
  progressData?: SignupLeadProgressV2;
};

export const signupMachineV2 = setup({
  types: {
    context: {} as InternalContext,
    events: {} as
      | { type: 'back' }
      | { type: 'submitState'; state: string }
      | { type: 'submitName'; firstName: string; lastName: string }
      | { type: 'submitEmail'; email: string; receiveNewsletter: boolean }
      | { type: 'submitPhoneNumber'; phoneNumber: string }
      | { type: 'submitTarget'; target: TherapyTarget }
      | { type: 'enterRunScreener'; runScreener: boolean },
    input: {} as SignupMachineInput,
  },

  actions: {
    saveLeadData: assign((_, { leadProgressData }: { leadProgressData: SignupLeadProgressV2 | null }) => {
      if (leadProgressData) {
        return {
          schedulingID: leadProgressData.schedulingId,
          progressData: leadProgressData,

          target: 'adult' as TherapyTarget,
          insuranceInfo: { useInsurance: false } as InsuranceResult['data'],
        };
      }
      return {};
    }),

    saveState: assign((_, params: { state: string }) => ({
      state: params.state,
    })),
    saveName: assign((_, params: { firstName: string; lastName: string }) => ({
      firstName: params.firstName,
      lastName: params.lastName,
    })),
    saveEmail: assign((_, params: { email: string; receiveNewsletter: boolean }) => ({
      email: params.email,
      receiveNewsletter: params.receiveNewsletter,
    })),

    savePhoneNumber: assign((_, params: { phoneNumber: string }) => ({
      phoneNumber: params.phoneNumber,
    })),

    saveTarget: assign((_, params: { target: TherapyTarget }) => ({
      target: params.target,
    })),

    saveMiniScreenerInfo: assign((_, params: { miniScreenerInfo: MiniScreenerResult }) => ({
      miniScreenerInfo: params.miniScreenerInfo.data,
    })),

    saveInsuranceInfo: assign((_, params: { insuranceInfo: InsuranceResult }) => ({
      insuranceInfo: params.insuranceInfo.data,
    })),

    saveScheduleInfo: assign((_, params: { scheduleInfo: ScheduleResult }) => ({
      scheduleInfo: params.scheduleInfo,
    })),

    saveLeadID: assign((_, params: { leadID: string; schedulingID: string }) => ({
      leadID: params.leadID,
      schedulingID: params.schedulingID,
    })),

    saveResumedStep: assign((_, params: { resumedStep: string }) => ({
      resumedStep: params.resumedStep,
    })),

    saveLeadIDToURL: (_, params: { leadID: string }) => {
      setLeadIDToURL(params.leadID);
    },

    clearLeadID: assign(() => ({
      leadID: undefined,
    })),

    updateLead: ({ context }) => {
      updateLead(context);
    },

    redirectOnComplete: (_, params: { context: CompletedSignupV2Data }) => {
      window.location.replace(getRedirectAfterSignupV2Complete(params.context));
    },
  },
  actors: {
    loadLeadData: loadLeadDataLogic,
    createLead: createLeadLogic,

    runMiniScreenerMachine: miniScreenerMachine,
    runInsuranceMachine: insuranceMachineV2,
    runScheduleMachine: scheduleMachineV2,
    scheduleConsultation: scheduleConsultationLogic,
  },
  guards: {
    hasLeadID: ({ context }) => isString(context.leadID),

    exitedByBack: (_, params: { earlyExit: boolean }) => params.earlyExit,

    canResumeToEnterTarget: getSignupResumeCheckGuard('phone'),
    canResumeToEnteringInsurance: getSignupResumeCheckGuard('communicationConcerns'),
    canResumeToEnteringSchedule: getSignupResumeCheckGuard('desiredPaymentMethod'),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5SwJZQHYFcAOBZAhgMYAWK6YAagEwB0sALvgE70DEA2gAwC6io2Ae1T0UA9HxAAPRAEYAbDJqdlnGQA4ArHI1VOAFjUB2ADQgAnoj0BOKzQDMVKlb1zDrzobtq5AXx+nUDBwCEjJKWgZmNnYZXiQQQWFRcXjpBHlFFVVNbV0DE3NENUUNLM5SqjU1Oz0ZPwC0LDwiUnJqGgAbAXwIMigAGTAegBF8RlYIMTAaMgA3AQBraa6ewZGx-C44-iEUETEJNLk9Tho1PS8ZGT1a9UdTCwQ9Qw0zl0utC5eX+pBAppCrXCNCYcEwAFs+gAFJgCKCg2CwDg8CSJPbJQ6yfS0Kg1DQyQxVKzeKgPRByNS0OTYuw2PRUQwyDQaX7-YItMLtBEQ6Gw+FwJExbYJXb7FKgNIybE0XF6fGEtTEuSkwpPKy0KycZWGKhS6yVFysxrs0JtWhgdD0MBMADKjCtrFgmAARpD6HaxmAtqjRRjUogHHY5EptTYrHJ1aoNGSnpw1DLdScKfLnHYjUFmqbgRarUwAHL4cFgR0ut0Fove+JosWYhBUDRWQw0LQMzy6TXhuQxk6vHXXLWaAmp9MAjlmmg563l4vOogLSs7JIHf0IOzXeOMzW1BlruR2GOlPQ0Aly2meOwedQjk1A9qTpgAUXB+BQHRLrr2T5fHQXIqX4qkRBcUbY8tFKNQ40MZw1APHRj0MOVak0IxaSsa9M1vc1LWtL9X1YWdCHnFEq19ZcJSAs9QO0ONIOgg9VBoVxT0bOwL0ZNR0MBTlaEIUExj6NYIAmKYZnQeYljoY0MO4mheKGER0AGIYIAQOYBEIfixC2X9qz9ci6w0TwaCgxl5DsNwKS7VVjnjGQGSYhDlBPTix2BOT+MUwTWGtWEmBobAOjGAAzAQmHBSSMy48d3IUpSelUsT1M09BtOIxd0TIwDV3XYyZC3OzPDMg9FTOAlDE8CN1TlQwXKzO9sKYKFiCmPMIWda18LnHTSIAyUFFeVxdAgxsrAyGDVQuYNKSccCoNpBtaswicGqalq2o6p0P3oVbyFa8F2qYbr-1rK55BlbRCUcYkbBeGMamDU8ZHMzgRspND-D+KSouzBqABVmBgNgCKI4VdMyyVpVleUiRJbt6RoJMqEqqCkf0RaZPvf6mEB983SxwGjoy3qsXhqGCRh5UY3rU5ShUel5EMco7BZD62Wk8d7wAYQEcFwUwdAUA0sVufQQhrXQJFgcJmsVzs5mlGecpHHMsaqeUJQsnbKVzKR9GOYa7nef5wXkpFsWmAl3G9kNvmBaF5IzfF2Bpb0rKMgGnUaJG1XVXrEosjlOQI3UDQ01Zr7XPq3M+gASQlzAmHwUXi0mchRPE6YmH5uOnUT5Pvpd8HZH6xjPeGqCfcePINZUE84yoHs9Z+6PFJzhOk7F4S07UiSs-QNu87FgvYh9Y6V2ApsmWoiCjDo33mWbAPdXcUOqCbqPrT6G0SEgTAOhTkSe8z-nt+IXf94LtK-yJ2s1wMXL8p3Ir59eWnlHpleHHXiId4gPe+hFk6Do9pkhd2mEfCKo46o-zPn-DoACxBAJAWIBK8x7ZaR4IXYmq5cgynulYBs5k+x6CpnuGgjZtARhuJSZUzxv50F-v-RSgC97IPQN5Jgvl-KBXoCFMKkCbwyVgIw+BzDEGsOSqgpKYpUqgx6rfXBsoIyEPKomO6cpyFuB0OxbQNg6i-HQAICAcAJBs2+tQUeN8VwAForKPGsa8MMTjnE2DDg0SKkcIiMBYJYmW+l6Qxm8MeIOTEbBaCsGeehKxeieWUqMRgvjXZpAcK-CJhIdCXlYscbsCElA6lGtVC8e4OLhw8dAkEYJISKRhHCBE8ASJj30tUXsNRnjfCcJwJG3YQKGScA3IMs8mT0PvB6K0iSi6ri8E2fEDZ+kEhonYywa4EbPGcBE8oN1hkNWnOM7BlQILkKcOGIhe5mTdnMkoNcrFSjqkZrSLZuZcIdF2bWXUmhjIRigucGoNQ3DnKbA4I5Lh6R7mVPQmKAllIvPHsUYMHgHDKHYjcZ4B5tD2DKghVwzMKhROUpzPiYoHycNCtC-SrEjDNiZFVNcjMIKLIQBoKopVCTkzspVNepSoFLXvDtMAe0DqkrdkGAa2g6UNk+eUOGrwkxXIQtTDwegHnWnxmAeggq0hGHjIQxU5ROlrk6VTK4zZaa6FlJwclirOWCP1rmG2xt0HoEdhbep6U-FCtYvYSolRGxOCMCQ32cZmzVHsua5puIOXuK5RjBqsd46DzAOq2QeiqIGGeEczVVMbgJiDk4BCmgXqVCVUwLeIiE0NKsfpBkcFNyqArhkrpvss2OBzY2AwNyWaRutcCYRsCmFQBYcA5KibJkUpmdS+ZdKqah2MkjQy5QKTFEQvQtmhsAqqrLa6pJAZFRHjSZSTF6hzVU1pAjLRjN6T1iDo2cF4jB2EuJUwYdXhnDkPMvulwh79yqlaZoi6T0VA6ENH4HwQA */
  id: 'signupMachineV2',

  initial: 'start',

  context: ({ input }) => ({
    leadID: getLeadIDFromURL() as string,
    machineConfig: {
      ...DEFAULT_CONFIG,
      ...input,
    },
    scheduleParameters: {
      appointmentType: input.appointmentType ?? DEFAULT_APPOINTMENT_TYPE,
      calendarID: input.calendarID,
    },
  }),

  states: {
    start: {
      always: [
        {
          guard: 'hasLeadID',
          target: 'loadingLeadData',
        },
        {
          target: 'enterState',
        },
      ],
    },

    loadingLeadData: {
      invoke: {
        src: 'loadLeadData',
        id: 'loadLeadData',
        input: ({ context: { leadID } }) => ({ leadID: leadID as string }),
        onDone: {
          target: 'enteringSchedule',
          actions: {
            type: 'saveLeadData',
            params: ({ event }) => ({ leadProgressData: event.output }),
          },
        },
      },
    },

    enterState: {
      on: {
        submitState: [
          {
            target: 'enterName',

            actions: {
              type: 'saveState',
              params: ({ event }) => event,
            },
          },
        ],
      },
    },
    enterName: {
      on: {
        submitName: {
          target: 'enterEmail',

          actions: {
            type: 'saveName',
            params: ({ event }) => event,
          },

          reenter: true,
        },

        back: 'enterState',
      },
    },

    enterEmail: {
      on: {
        submitEmail: [
          {
            target: 'enterPhoneNumber',
            actions: {
              type: 'saveEmail',
              params: ({ event }) => event,
            },
          },
        ],
        back: 'enterName',
      },
    },

    enterPhoneNumber: {
      on: {
        back: 'enterEmail',

        submitPhoneNumber: [
          {
            target: 'creatingLead',
            guard: not('hasLeadID'),
            actions: {
              type: 'savePhoneNumber',
              params: ({ event }) => event,
            },
          },
          {
            target: 'enterTarget',
            actions: [
              {
                type: 'savePhoneNumber',
                params: ({ event }) => event,
              },
              'updateLead',
            ],
          },
        ],
      },
    },

    creatingLead: {
      invoke: {
        src: 'createLead',

        input: ({ context }) => context as CompletedSignupV2Data,
        onDone: {
          target: 'enterTarget',

          actions: [
            {
              type: 'saveLeadID',
              params: ({ event }) => ({ leadID: event.output.id, schedulingID: event.output.schedulingId }),
            },
            {
              type: 'saveLeadIDToURL',
              params: ({ event }) => ({ leadID: event.output.id }),
            },
          ],
        },
        onError: {
          target: 'leadCreationError',
        },
      },
    },

    leadCreationError: {},

    enterTarget: {
      on: {
        back: 'enterPhoneNumber',

        submitTarget: {
          target: 'enteringMiniScreener',
          actions: [
            {
              type: 'saveTarget',
              params: ({ event }) => event,
            },
            'updateLead',
          ],
        },
      },
    },

    enteringMiniScreener: {
      invoke: {
        id: 'runMiniScreenerMachine',
        src: 'runMiniScreenerMachine',

        input: ({ context }) => ({
          target: context.target as TherapyTarget,
          prevState: { state: context.state, ...context.miniScreenerInfo },
          canExitByBack: context.resumedStep !== 'enteringMiniScreener',
        }),

        onDone: [
          {
            target: 'enterTarget',
            guard: {
              type: 'exitedByBack',
              params: ({ event: { output } }) => ({ earlyExit: output.earlyExit ?? false }),
            },
            actions: {
              type: 'saveMiniScreenerInfo',
              params: ({ event }) => ({ miniScreenerInfo: event.output }),
            },
          },

          {
            target: 'enteringInsurance',
            actions: [
              {
                type: 'saveMiniScreenerInfo',
                params: ({ event }) => ({ miniScreenerInfo: event.output }),
              },
              'updateLead',
            ],
          },
        ],
      },
    },

    enteringInsurance: {
      invoke: {
        id: 'runInsuranceMachine',
        src: 'runInsuranceMachine',

        input: ({ context }) => ({
          prevState: { state: context.state, ...context.insuranceInfo },
          canExitByBack: context.resumedStep !== 'enteringInsurance',
        }),

        onDone: [
          {
            target: 'enteringMiniScreener',
            guard: {
              type: 'exitedByBack',
              params: ({ event: { output } }) => ({ earlyExit: output.earlyExit ?? false }),
            },
            actions: {
              type: 'saveInsuranceInfo',
              params: ({ event }) => ({ insuranceInfo: event.output }),
            },
          },

          {
            target: 'enteringSchedule',
            actions: [
              {
                type: 'saveInsuranceInfo',
                params: ({ event }) => ({ insuranceInfo: event.output }),
              },
              'updateLead',
            ],
          },
        ],
      },
    },

    enteringSchedule: {
      invoke: {
        id: 'runScheduleMachine',
        src: 'runScheduleMachine',

        input: ({ context }) => ({
          machineConfig: {
            appointmentType: context.machineConfig.appointmentType as string,
            calendarID: context.machineConfig.calendarID,
            canExitByBack: context.resumedStep !== 'enteringSchedule',
          },
        }),

        onDone: [
          {
            target: 'enteringInsurance',
            guard: {
              type: 'exitedByBack',
              params: ({ event: { output } }) => ({ earlyExit: output.earlyExit ?? false }),
            },
          },
          {
            target: 'schedulingConsultation',
            actions: {
              type: 'saveScheduleInfo',
              params: ({ event }) => ({ scheduleInfo: event.output.data }),
            },
          },
        ],
      },
    },

    schedulingConsultation: {
      invoke: {
        src: 'scheduleConsultation',
        input: ({ context }) => context as CompletedSignupV2Data,
        onDone: 'signupComplete',
        onError: 'consultationError',
      },
    },

    signupComplete: {
      entry: {
        type: 'redirectOnComplete',
        params: ({ context }) => ({ context: context as CompletedSignupV2Data }),
      },
    },

    consultationError: {},
  },
});

export type SignupState = MachineStateType<typeof signupMachineV2>;
